import React, { lazy } from "react";
import { FiHome, FiTool, FiInfo, FiMessageSquare } from "react-icons/fi";
import { FaRegQuestionCircle, FaRegLightbulb } from "react-icons/fa";

import About from "../staticpages/About.jsx";
import Examples from "../staticpages/Examples.jsx";
import Help from "../staticpages/Help.jsx";
import NoPage from "../staticpages/NoPage.jsx";
import { Feedback } from "@cadenza/shared-staticpages";

const RADManager = lazy(() =>
  import("@cadenza/rad-react").then((m) => ({
    default: m.RADManager ?? m.default,
  }))
);

const Model = lazy(() =>
  import("@cadenza/riva-react").then((m) => ({
    default: m.ModelI18n ?? m.Model ?? m.default,
  }))
);

/**
 * @typedef {object} AppRoute
 * @property {string} path
 * @property {React.ReactNode} element
 * @property {boolean} [showInNav]
 * @property {string} [label]
 * @property {React.ComponentType} [icon]
 * @property {boolean} [filled]
 */

/**
 * routes: full route definitions used by <Routes />
 * - element: React element to render
 * - path: route path
 * - showInNav: whether to include in navigation
 * - label/icon: optional nav metadata
 * - filled: whether the icon should be filled,
 * -    so you need to know the proclivities of the icon set
 *
 * @type {AppRoute[]}
 */
const routes = [
  {
    path: "/",
    element: <Model />,
    label: "Riva",
    icon: FiHome,
    showInNav: true,
  },
  {
    path: "rad/:radId?",
    element: <RADManager />,
    label: "RAD",
    icon: FiTool,
    showInNav: true,
  },
  {
    path: "about",
    element: <About />,
    label: "About",
    icon: FiInfo,
    showInNav: true,
  },
  {
    path: "examples",
    element: <Examples />,
    label: "Examples",
    icon: FaRegLightbulb,
    filled: true,
    showInNav: true,
  },
  {
    path: "feedback",
    element: <Feedback />,
    label: "Feedback",
    icon: FiMessageSquare,
    showInNav: true,
  },
  {
    path: "help",
    element: <Help />,
    label: "Help",
    icon: FaRegQuestionCircle,
    filled: true,
    showInNav: true,
  },
  { path: "*", element: <NoPage />, showInNav: false },
];

export const validateRoutes = (routeList) => {
  if (!Array.isArray(routeList)) {
    throw new TypeError("routes must be an array");
  }

  const seenPaths = new Set();

  for (const route of routeList) {
    if (!route || typeof route.path !== "string" || !route.path.trim()) {
      throw new Error("each route must include a non-empty string path");
    }
    if (!("element" in route)) {
      throw new Error(`route '${route.path}' must include an element`);
    }
    if (seenPaths.has(route.path)) {
      throw new Error(`duplicate route path '${route.path}'`);
    }
    seenPaths.add(route.path);

    if (route.showInNav && !route.label) {
      throw new Error(`route '${route.path}' must include a label when showInNav is true`);
    }
  }

  return routeList;
};

validateRoutes(routes);

export default routes;

// navLinks: shape expected by NavBar/Sidebar (to, label, icon)
export const navLinks = routes
  .filter((r) => r.showInNav)
  .map((r) => ({
    to: r.path === "/" ? "/" : `/${r.path.replace(/^\/+/, "")}`,
    label: r.label,
    icon: r.icon,
    filled: !!r.filled,
  }));
